// Aba "Dados de Campo" dentro do contrato (DADOS-CAMPO-2, 2026-06-15).
// Mostra os anexos e links (da caixa dados@) roteados PRA ESTE contrato.
// A equipe pega o dado e marca como baixado; o histórico evita retrabalho.
// A roteação item→contrato é feita na página global (Operacional → Dados de Campo).

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Button, Card, Empty, Flex, Space, Spin, Table, Tag, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined, DownloadOutlined, LinkOutlined, PaperClipOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import { API_URL } from '../../config';
import type { Contrato } from './types';
import { getToken } from './utils';

const { Text } = Typography;

interface ItemBase {
  id: number;
  email_id: number;
  assunto: string | null;
  remetente_nome: string | null;
  remetente_email: string | null;
  data_recebido: string;
  n_downloads: number;
  ultimo_baixado_por: string | null;
  ultimo_download_em: string | null;
}
interface AnexoItem extends ItemBase { nome: string; mime_type: string | null; tamanho_bytes: number | null; }
interface LinkItem extends ItemBase { url: string; dominio: string | null; texto_ancora: string | null; }

async function chamarApi<T = any>(action: string, payload: Record<string, any> = {}): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/email-dados`, { action, ...payload },
    { headers: { Authorization: token, 'Content-Type': 'application/json' } });
  const raw: any = r.data;
  return (raw?.body ? JSON.parse(raw.body) : raw) as T;
}

function fmtTamanho(b: number | null): string {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
function fmtQuando(d: string | null): string {
  if (!d) return '';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

function ColunaBaixado({ item }: { item: ItemBase }) {
  if (!item.ultimo_baixado_por) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
  const nome = (item.ultimo_baixado_por || '').split('@')[0];
  return (
    <Tooltip title={`${item.n_downloads} download(s). Último por ${item.ultimo_baixado_por}`}>
      <Text style={{ fontSize: 11, color: '#0a7f4a' }}>✅ <strong>{nome}</strong> {fmtQuando(item.ultimo_download_em)}</Text>
    </Tooltip>
  );
}

export function AbaDadosCampo({ contrato }: { contrato: Contrato }) {
  return <AntApp><AbaDadosCampoInner contrato={contrato} /></AntApp>;
}

function AbaDadosCampoInner({ contrato }: { contrato: Contrato }) {
  const { message } = AntApp.useApp();
  const [anexos, setAnexos] = useState<AnexoItem[]>([]);
  const [links, setLinks] = useState<LinkItem[]>([]);
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await chamarApi<{ anexos: AnexoItem[]; links: LinkItem[] }>('por_contrato', { contrato_id: contrato.id });
      setAnexos(r.anexos || []);
      setLinks(r.links || []);
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [contrato.id, message]);
  useEffect(() => { carregar(); }, [carregar]);

  async function baixarAnexo(a: AnexoItem) {
    try {
      const r = await chamarApi<{ url: string }>('anexo_url', { anexo_id: a.id });
      window.open(r.url, '_blank');
      carregar();
    } catch (e: any) {
      message.error('Falha ao baixar: ' + (e.response?.data?.error || e.message));
    }
  }
  async function marcarLinkBaixado(l: LinkItem) {
    try {
      await chamarApi('link_marcar_baixado', { link_id: l.id });
      message.success('Marcado como baixado');
      carregar();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    }
  }

  const colOrigem = (row: ItemBase) => (
    <div>
      <Text style={{ fontSize: 12, fontWeight: 500 }}>{row.remetente_nome || row.remetente_email || '—'}</Text>
      <div><Text type="secondary" style={{ fontSize: 11 }}>{row.assunto || '(sem assunto)'} · {fmtQuando(row.data_recebido)}</Text></div>
    </div>
  );

  // Lista ÚNICA: anexos + links no mesmo lugar, distinguidos por tipo (item 2).
  type ItemUnificado =
    | (AnexoItem & { _tipo: 'anexo' })
    | (LinkItem & { _tipo: 'link' });
  const itens: ItemUnificado[] = [
    ...anexos.map(a => ({ ...a, _tipo: 'anexo' as const })),
    ...links.map(l => ({ ...l, _tipo: 'link' as const })),
  ].sort((x, y) => (y.data_recebido || '').localeCompare(x.data_recebido || ''));

  const colunas: ColumnsType<ItemUnificado> = [
    { title: 'Item', key: 'item', render: (_, it) => it._tipo === 'anexo' ? (
      <Space>
        <PaperClipOutlined />
        <Text>{(it as AnexoItem).nome}</Text>
        <Text type="secondary" style={{ fontSize: 11 }}>{fmtTamanho((it as AnexoItem).tamanho_bytes)}</Text>
      </Space>
    ) : (
      <Space>
        <LinkOutlined style={{ color: '#0930D0' }} />
        {(it as LinkItem).dominio && <Tag color="blue" style={{ margin: 0 }}>{(it as LinkItem).dominio}</Tag>}
        <a href={(it as LinkItem).url} target="_blank" rel="noopener noreferrer"
          style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'bottom' }}>
          {(it as LinkItem).texto_ancora || (it as LinkItem).url}
        </a>
      </Space>
    ) },
    { title: 'Tipo', key: 'tipo', width: 90,
      render: (_, it) => it._tipo === 'anexo'
        ? <Tag>📎 Anexo</Tag>
        : <Tag color="blue">🔗 Link</Tag> },
    { title: 'Origem (e-mail)', key: 'origem', render: (_, it) => colOrigem(it) },
    { title: 'Baixado', key: 'baixado', width: 170, render: (_, it) => <ColunaBaixado item={it} /> },
    { title: '', key: 'acao', width: 150, align: 'right', render: (_, it) => it._tipo === 'anexo' ? (
      <Button size="small" icon={<DownloadOutlined />} onClick={() => baixarAnexo(it as AnexoItem)}>Baixar</Button>
    ) : (
      <Button size="small" type={it.n_downloads > 0 ? 'default' : 'primary'}
        icon={<CheckCircleOutlined />} onClick={() => marcarLinkBaixado(it as LinkItem)}>
        {it.n_downloads > 0 ? 'Baixar de novo' : 'Marcar baixado'}
      </Button>
    ) },
  ];

  if (loading) return <Flex justify="center" style={{ padding: 40 }}><Spin /></Flex>;

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Dados de campo (anexos e links) roteados a este contrato a partir da caixa
          <code> dados@</code>. A roteação por item é feita em Operacional → Dados de Campo.
        </Text>
        <Button size="small" icon={<ReloadOutlined />} onClick={carregar} />
      </Flex>

      {itens.length === 0 ? (
        <Empty description="Nenhum dado de campo roteado a este contrato ainda." />
      ) : (
        <Table<ItemUnificado> size="small" rowKey={(it) => `${it._tipo}-${it.id}`}
          columns={colunas} dataSource={itens} pagination={false} />
      )}
    </div>
  );
}
